/**
 * @file JoinUs.page.tsx
 * @path apps/website/src/pages/JoinUs.page.tsx
 * @description Master page orchestrating the recruitment layout via Facade hook.
 */

// Core Layout & SEO
import { SeoHelmet as SEOHelmet } from "@/components";
import { NAV_LINKS } from "@/config";

// Assets
import { shuttle3 } from "@/assets/images";
import { scene1 } from "@/assets/videos";

// Sections & Components
import { CallToAction, HeroSection } from "@/components/sections";
import { OpenPositions } from "@/features/recruitment/components/openPositions.component";
import { RecruitmentProcess } from "@/features/recruitment/components/recruitmentProcess.component";
import { JobApplicationDialog } from "@/features/recruitment/components/jobApplicationDialog.component";

// Facade Hook
import { useRecruitmentFacade } from "@/features/recruitment/hooks/useRecruitmentFacade.hook";

const JoinUs = () => {
  // Call the single orchestrator hook for the entire feature
  const facade = useRecruitmentFacade();

  return (
    <>
      <SEOHelmet
        pageType="careers"
        title="Careers: Join Our Driving Team | Sir Bastian's Shuttle Service"
        description="We're hiring! Explore open positions for drivers and coordinators at Sir Bastians Shuttle Service in Cape Town and apply today."
        url={NAV_LINKS.joinUs.path}
      />

      <HeroSection
        videoSrc={scene1}
        title="Drive Your Career"
        subtitle="Forward"
        paragraph="Join our team of professional drivers and become part of Cape Town's premier shuttle service"
        actions={[
          { text: "View Vacancies", scrollTo: "open-positions" },
          { text: "Our Process", scrollTo: "recruitment-process" },
        ]}
      />

      {/* Inject specific facade slices into dumb components */}
      <OpenPositions facade={facade.positions} />

      <RecruitmentProcess facade={facade.process} />

      {/* The Dialog lives at the page root, controlled by the application facade */}
      <JobApplicationDialog facade={facade.application} />

      {/* CTA Section */}
      <CallToAction
        title="Join a Team That Moves Cape Town"
        description="We believe that great journeys are powered by great people. If you're passionate about providing exceptional service and want to be part of a professional, growing team, we'd love to meet you."
        primaryAction={{
          text: NAV_LINKS.contactUs.label,
          href: NAV_LINKS.contactUs.path,
        }}
        backgroundImage={{
          src: shuttle3,
          alt: "A diverse and professional team of drivers standing together",
        }}
      />
    </>
  );
};

export default JoinUs;
